/**
 * <s-review-form> — Sends a product review to Judge.me's public review endpoint.
 *
 * Attributes:
 *   data-endpoint      — Judge.me create-review URL.
 *   data-sending-label — Submit button label while the request is in flight.
 *
 * Required child markup:
 *   form                  — Hidden fields shop_domain, url, platform, id plus rating, name, email, title, body.
 *   [data-review-error]   — Hidden error message, shown on network failure.
 *   [data-review-success] — Hidden success message, shown after sending.
 *
 * The request is sent as a simple form-encoded POST in no-cors mode, so the response is opaque;
 * a resolved request counts as sent and Judge.me applies the shop's moderation settings.
 * When the surrounding <dialog> closes, the form is shown again for the next review.
 */
class SReviewForm extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    const form = this.querySelector('form');
    if (!form) return;

    form.addEventListener('submit', (event) => this.#submit(event, form), { signal });
    form.addEventListener('input', (event) => {
      if (event.target instanceof HTMLElement) event.target.removeAttribute('aria-invalid');
    }, { signal });

    this.closest('dialog')?.addEventListener('close', () => this.#reset(form), { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #submit(event, form) {
    event.preventDefault();
    const error = this.querySelector('[data-review-error]');
    error?.classList.add('hidden');

    const invalid = [...form.elements].filter(
      (el) => (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) && !el.checkValidity()
    );
    invalid.forEach((el) => el.setAttribute('aria-invalid', 'true'));
    if (invalid.length) {
      invalid[0].focus();
      return;
    }

    const button = form.querySelector('[type="submit"]');
    const originalLabel = button?.textContent ?? '';
    if (button) {
      button.disabled = true;
      button.textContent = this.dataset.sendingLabel || originalLabel;
    }

    try {
      await fetch(this.dataset.endpoint, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(new FormData(form)),
      });
      form.classList.add('hidden');
      const success = this.querySelector('[data-review-success]');
      success?.classList.remove('hidden');
      success?.focus();
    } catch {
      error?.classList.remove('hidden');
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = originalLabel;
      }
    }
  }

  #reset(form) {
    form.reset();
    form.classList.remove('hidden');
    form.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
    this.querySelector('[data-review-success]')?.classList.add('hidden');
    this.querySelector('[data-review-error]')?.classList.add('hidden');
  }
}

if (!customElements.get('s-review-form')) customElements.define('s-review-form', SReviewForm);
